// Seed data from the spec's CostEscalation_Engine.xlsx (2026-05-21).
// All identifiers, names, weights, dates, prices, and index values are verbatim from the workbook.

/* eslint-disable max-len */

export interface EngineSettings {
  bidName: string;
  baselineDate: string;
  reviewDate: string;
  forwardMonths: number;
  confidenceMultiplier: number;
  minPurchasePoints: number;
  minMonths: number;
  highConfPoints: number;
  highConfMonths: number;
  weightMovement: number;
  weightData: number;
  weightCoverage: number;
  weightRecovery: number;
}

export const DEFAULT_SETTINGS: EngineSettings = {
  bidName: 'Bid 2026-Q1 (illustrative)',
  baselineDate: '2024-01-01',
  reviewDate: '2026-01-01',
  forwardMonths: 12,
  confidenceMultiplier: 1.0,
  minPurchasePoints: 2,
  minMonths: 6,
  highConfPoints: 5,
  highConfMonths: 18,
  weightMovement: 0.4,
  weightData: 0.2,
  weightCoverage: 0.2,
  weightRecovery: 0.2,
};

export interface IndexDef {
  id: string;
  name: string;
  type: 'Material' | 'Labour';
  materialWeight: number;
  labourWeight: number;
  notes: string;
}

export const INDICES_SEED: IndexDef[] = [
  { id: 'WPU10', name: 'WPU10 Metals & Metal Products', type: 'Material', materialWeight: 0.3, labourWeight: 0, notes: 'BLS PPI' },
  { id: 'WPUID61113', name: 'WPUID61113 Materials Durable Mfg', type: 'Material', materialWeight: 0.3, labourWeight: 0, notes: 'BLS PPI' },
  { id: 'ECI', name: 'ECI Wages Private Industry', type: 'Labour', materialWeight: 0, labourWeight: 1.0, notes: 'BLS ECI' },
  { id: 'ElecEquip', name: 'Electrical Equipment AHE', type: 'Material', materialWeight: 0.15, labourWeight: 0, notes: 'BLS CES' },
  { id: 'OtherElec', name: 'Other Electrical AHE', type: 'Material', materialWeight: 0.05, labourWeight: 0, notes: 'BLS CES' },
  { id: 'TransEquip', name: 'Transportation Equip AHE', type: 'Material', materialWeight: 0.2, labourWeight: 0, notes: 'BLS CES' },
];

export interface ComparableGroup {
  id: string;
  name: string;
  description: string;
}

export const COMPARABLE_GROUPS_SEED: ComparableGroup[] = [
  { id: 'PG.Cab.Steel', name: 'Cab steel weldments', description: 'Steel cab structural assemblies' },
  { id: 'PG.Trac.Motor', name: 'Traction motor copper assemblies', description: 'Wound copper rotors/stators' },
  { id: 'PG.HVAC.Comp', name: 'HVAC compressors', description: 'Climate-control compressors' },
  { id: 'PG.Brake.Friction', name: 'Brake friction materials', description: 'Brake pads and shoes' },
  { id: 'PG.Conn.HV', name: 'High-voltage connectors', description: 'Bus bars and connectors above 600V' },
];

export type PassThroughStatus = 'Yes' | 'Partial' | 'No';

export interface Part {
  id: string;
  name: string;
  sku: string;
  supplier: string;
  comparableGroupId: string | null;
  primaryIndexId: string | null;
  secondaryIndexId: string | null;
  passThrough: PassThroughStatus;
  recoveryMechanism: string;
  inBidScope: boolean;
  notes: string;
}

export const PARTS_SEED: Part[] = [
  { id: 'P001', name: 'Cab side panel left', sku: 'MEPPI-CAB-001', supplier: 'Mitsubishi', comparableGroupId: 'PG.Cab.Steel', primaryIndexId: 'WPU10', secondaryIndexId: null, passThrough: 'Yes', recoveryMechanism: '§24.4 escalation', inBidScope: true, notes: 'Illustrative seed' },
  { id: 'P002', name: 'Cab side panel right', sku: 'MEPPI-CAB-002', supplier: 'Mitsubishi', comparableGroupId: 'PG.Cab.Steel', primaryIndexId: 'WPU10', secondaryIndexId: 'WPUID61113', passThrough: 'Yes', recoveryMechanism: '§24.4 escalation', inBidScope: true, notes: 'Illustrative seed' },
  { id: 'P003', name: 'Traction motor rotor', sku: 'MEPPI-TRC-100', supplier: 'Mitsubishi', comparableGroupId: 'PG.Trac.Motor', primaryIndexId: 'WPUID61113', secondaryIndexId: 'WPU10', passThrough: 'Partial', recoveryMechanism: '§24.4 partial', inBidScope: true, notes: 'Copper component' },
  { id: 'P004', name: 'HVAC compressor', sku: 'BLK-HVAC-22', supplier: 'Blackstone', comparableGroupId: 'PG.HVAC.Comp', primaryIndexId: 'ElecEquip', secondaryIndexId: null, passThrough: 'Partial', recoveryMechanism: '§24.4 partial', inBidScope: true, notes: '' },
  { id: 'P005', name: 'Brake pad set', sku: 'RAM-BRK-501', supplier: 'RamIndustrial', comparableGroupId: 'PG.Brake.Friction', primaryIndexId: 'TransEquip', secondaryIndexId: null, passThrough: 'No', recoveryMechanism: 'Fixed price', inBidScope: true, notes: 'Single-source risk' },
  { id: 'P006', name: 'HV connector', sku: 'SDE-CON-330', supplier: 'SealAndDesign', comparableGroupId: 'PG.Conn.HV', primaryIndexId: 'ElecEquip', secondaryIndexId: 'OtherElec', passThrough: 'Yes', recoveryMechanism: '§24.4 escalation', inBidScope: true, notes: '' },
  { id: 'P007', name: 'Wiring loom A', sku: 'SDE-LOOM-A', supplier: 'SealAndDesign', comparableGroupId: 'PG.Conn.HV', primaryIndexId: 'OtherElec', secondaryIndexId: null, passThrough: 'Yes', recoveryMechanism: '§24.4 escalation', inBidScope: false, notes: 'Out of bid scope' },
  { id: 'P008', name: 'Floor structural beam', sku: 'MEPPI-FLR-080', supplier: 'Mitsubishi', comparableGroupId: 'PG.Cab.Steel', primaryIndexId: 'WPU10', secondaryIndexId: null, passThrough: 'Yes', recoveryMechanism: '§24.4 escalation', inBidScope: true, notes: '' },
];

export interface PurchaseEvent {
  partId: string;
  date: string;
  unitPrice: number;
  quantity: number;
  supplier: string;
}

export const PURCHASES_SEED: PurchaseEvent[] = [
  { partId: 'P001', date: '2024-01-15', unitPrice: 240, quantity: 50, supplier: 'Mitsubishi' },
  { partId: 'P001', date: '2024-08-01', unitPrice: 245.5, quantity: 75, supplier: 'Mitsubishi' },
  { partId: 'P001', date: '2025-04-01', unitPrice: 258, quantity: 60, supplier: 'Mitsubishi' },
  { partId: 'P001', date: '2025-12-01', unitPrice: 267.2, quantity: 40, supplier: 'Mitsubishi' },
  { partId: 'P002', date: '2024-02-01', unitPrice: 235, quantity: 50, supplier: 'Mitsubishi' },
  { partId: 'P002', date: '2025-01-01', unitPrice: 249, quantity: 60, supplier: 'Mitsubishi' },
  { partId: 'P002', date: '2025-11-01', unitPrice: 265, quantity: 45, supplier: 'Mitsubishi' },
  { partId: 'P003', date: '2025-06-01', unitPrice: 1850, quantity: 8, supplier: 'Mitsubishi' },
  { partId: 'P004', date: '2024-01-10', unitPrice: 4200, quantity: 4, supplier: 'Blackstone' },
  { partId: 'P004', date: '2024-06-01', unitPrice: 4280, quantity: 4, supplier: 'Blackstone' },
  { partId: 'P004', date: '2024-12-01', unitPrice: 4380, quantity: 6, supplier: 'Blackstone' },
  { partId: 'P004', date: '2025-06-01', unitPrice: 4520, quantity: 5, supplier: 'Blackstone' },
  { partId: 'P004', date: '2025-12-01', unitPrice: 4690, quantity: 4, supplier: 'Blackstone' },
  { partId: 'P005', date: '2024-01-01', unitPrice: 85, quantity: 200, supplier: 'RamIndustrial' },
  { partId: 'P005', date: '2024-04-01', unitPrice: 88, quantity: 250, supplier: 'RamIndustrial' },
  { partId: 'P005', date: '2024-08-01', unitPrice: 95, quantity: 180, supplier: 'RamIndustrial' },
  { partId: 'P005', date: '2025-01-01', unitPrice: 105, quantity: 220, supplier: 'RamIndustrial' },
  { partId: 'P005', date: '2025-06-01', unitPrice: 115, quantity: 200, supplier: 'RamIndustrial' },
  { partId: 'P005', date: '2025-12-01', unitPrice: 124, quantity: 180, supplier: 'RamIndustrial' },
  { partId: 'P006', date: '2024-03-01', unitPrice: 62, quantity: 120, supplier: 'SealAndDesign' },
  { partId: 'P006', date: '2024-11-01', unitPrice: 65.2, quantity: 130, supplier: 'SealAndDesign' },
  { partId: 'P006', date: '2025-10-01', unitPrice: 68.5, quantity: 110, supplier: 'SealAndDesign' },
  { partId: 'P007', date: '2024-05-01', unitPrice: 320, quantity: 35, supplier: 'SealAndDesign' },
  { partId: 'P007', date: '2025-08-01', unitPrice: 338, quantity: 30, supplier: 'SealAndDesign' },
  { partId: 'P008', date: '2025-09-01', unitPrice: 480, quantity: 25, supplier: 'Mitsubishi' },
];

export interface IndexHistoryPoint {
  indexId: string;
  date: string;
  value: number;
  source: string;
}

// ─── Clause Register (Item 5 / §1.7.3-new) ───────────────────────────────────
export interface ClauseRegisterEntry {
  clauseId: string;
  title: string;
  contractualBasis: string;
  appliesTo: string;
  escalationMechanism: string;
}

export const SEED_CLAUSE_REGISTER: ClauseRegisterEntry[] = [
  { clauseId: '§24.4', title: 'Cost escalation', contractualBasis: 'Authority Contract — cost escalation mechanism (non-negotiable per Authority Terms).', appliesTo: 'P001, P002, P003, P006, P007, P008', escalationMechanism: '§24.4 escalation' },
  { clauseId: '§24.4.5', title: 'Basket reconciler', contractualBasis: 'Authority Contract — frame-and-body supplier basket math.', appliesTo: 'P001, P002, P008', escalationMechanism: '§24.4.5 basket' },
  { clauseId: '§24.4 partial', title: 'Partial pass-through', contractualBasis: 'Authority Contract — escalation with risk-sharing.', appliesTo: 'P003, P004', escalationMechanism: '§24.4 partial' },
  { clauseId: 'Fixed price', title: 'Fixed price (no escalation)', contractualBasis: 'Authority Contract — locked price, supplier carries movement risk.', appliesTo: 'P005', escalationMechanism: 'Fixed price' },
];

export const INDEX_HISTORY_SEED: IndexHistoryPoint[] = [
  { indexId: 'WPU10', date: '2023-01-01', value: 220, source: 'BLS (illustrative seed)' },
  { indexId: 'WPU10', date: '2023-07-01', value: 228, source: 'BLS (illustrative seed)' },
  { indexId: 'WPU10', date: '2024-01-01', value: 235, source: 'BLS (illustrative seed)' },
  { indexId: 'WPU10', date: '2024-07-01', value: 244, source: 'BLS (illustrative seed)' },
  { indexId: 'WPU10', date: '2025-01-01', value: 256, source: 'BLS (illustrative seed)' },
  { indexId: 'WPU10', date: '2025-07-01', value: 268, source: 'BLS (illustrative seed)' },
  { indexId: 'WPU10', date: '2026-01-01', value: 281, source: 'BLS (illustrative seed)' },
  { indexId: 'WPUID61113', date: '2023-01-01', value: 195, source: 'BLS (illustrative seed)' },
  { indexId: 'WPUID61113', date: '2023-07-01', value: 200, source: 'BLS (illustrative seed)' },
  { indexId: 'WPUID61113', date: '2024-01-01', value: 207, source: 'BLS (illustrative seed)' },
  { indexId: 'WPUID61113', date: '2024-07-01', value: 215, source: 'BLS (illustrative seed)' },
  { indexId: 'WPUID61113', date: '2025-01-01', value: 223, source: 'BLS (illustrative seed)' },
  { indexId: 'WPUID61113', date: '2025-07-01', value: 231, source: 'BLS (illustrative seed)' },
  { indexId: 'WPUID61113', date: '2026-01-01', value: 240, source: 'BLS (illustrative seed)' },
  { indexId: 'ECI', date: '2023-01-01', value: 152, source: 'BLS (illustrative seed)' },
  { indexId: 'ECI', date: '2023-07-01', value: 154.5, source: 'BLS (illustrative seed)' },
  { indexId: 'ECI', date: '2024-01-01', value: 157, source: 'BLS (illustrative seed)' },
  { indexId: 'ECI', date: '2024-07-01', value: 159.5, source: 'BLS (illustrative seed)' },
  { indexId: 'ECI', date: '2025-01-01', value: 162, source: 'BLS (illustrative seed)' },
  { indexId: 'ECI', date: '2025-07-01', value: 164.5, source: 'BLS (illustrative seed)' },
  { indexId: 'ECI', date: '2026-01-01', value: 167, source: 'BLS (illustrative seed)' },
  { indexId: 'ElecEquip', date: '2023-01-01', value: 35, source: 'BLS (illustrative seed)' },
  { indexId: 'ElecEquip', date: '2024-01-01', value: 36.4, source: 'BLS (illustrative seed)' },
  { indexId: 'ElecEquip', date: '2025-01-01', value: 37.9, source: 'BLS (illustrative seed)' },
  { indexId: 'ElecEquip', date: '2026-01-01', value: 39.5, source: 'BLS (illustrative seed)' },
  { indexId: 'OtherElec', date: '2023-01-01', value: 33, source: 'BLS (illustrative seed)' },
  { indexId: 'OtherElec', date: '2024-01-01', value: 34.3, source: 'BLS (illustrative seed)' },
  { indexId: 'OtherElec', date: '2025-01-01', value: 35.7, source: 'BLS (illustrative seed)' },
  { indexId: 'OtherElec', date: '2026-01-01', value: 37.2, source: 'BLS (illustrative seed)' },
  { indexId: 'TransEquip', date: '2023-01-01', value: 40, source: 'BLS (illustrative seed)' },
  { indexId: 'TransEquip', date: '2024-01-01', value: 41.5, source: 'BLS (illustrative seed)' },
  { indexId: 'TransEquip', date: '2025-01-01', value: 43.1, source: 'BLS (illustrative seed)' },
  { indexId: 'TransEquip', date: '2026-01-01', value: 44.8, source: 'BLS (illustrative seed)' },
];
